import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  CheckCircle, 
  Clock, 
  FileText, 
  Star, 
  TrendingUp,
  Users,
  DollarSign,
  Filter,
  Grid3X3,
  List,
  Plus,
  Download,
  Eye,
  Edit,
  Trash2,
  MessageCircle,
  Receipt,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Contract } from '../../types/contract';
import { useContracts } from '../../contexts/ContractContext';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { 
  baseTransitions, 
  entranceAnimations, 
  hoverAnimations, 
  cardAnimations,
  buttonAnimations 
} from '../../utils/animations';
import StatsCard from '../ui/StatsCard';
import ContractCard from '../ui/ContractCard';
import DataTable, { Column } from '../ui/DataTable';
import { 
  StatsCardSkeleton, 
  CardsGridSkeleton, 
  DataTableSkeleton 
} from '../ui/Skeleton';

const DashboardNew: React.FC = () => {
  const navigate = useNavigate();
  const { contracts, loading, refreshContracts, updateContract, deleteContract } = useContracts();
  const [showSkeletons, setShowSkeletons] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedContracts, setSelectedContracts] = useState<Contract[]>([]);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [filters, setFilters] = useState({
    status: '',
    payment: '',
    dateRange: '',
    search: ''
  });

  useEffect(() => {
    if (!loading) {
      // Delay inicial para os skeletons aparecerem gradualmente
      setTimeout(() => {
        setShowSkeletons(false);
      }, 150);
    }
  }, [loading]);

  // Filter contracts based on current filters
  const filteredContracts = contracts.filter(contract => {
    if (filters.status && contract.status !== filters.status) return false;
    if (filters.payment === 'pago' && !contract.pago) return false;
    if (filters.payment === 'pendente' && contract.pago) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        contract.contratante?.toLowerCase().includes(searchLower) ||
        contract.nomeAniversariante?.toLowerCase().includes(searchLower) ||
        contract.cpf?.includes(searchLower) ||
        contract.telefone?.includes(searchLower) ||
        contract.endereco?.toLowerCase().includes(searchLower) ||
        contract.cidade?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Parse contract date function (same logic as Angular project)
  const parseContractDate = (dateString: string): Date => {
    if (!dateString) return new Date();
    
    // Try DD/MM/YYYY format first
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    
    // Fallback to ISO or other formats
    return new Date(dateString);
  };

  // Sort contracts using the same logic as Angular project
  const sortedContracts = [...filteredContracts].sort((a, b) => {
    const dateA = parseContractDate(a.dataFesta || '');
    const dateB = parseContractDate(b.dataFesta || '');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if events are future or past
    const isAFuture = dateA >= today;
    const isBFuture = dateB >= today;
    
    // Prioritize future events over past ones
    if (isAFuture && !isBFuture) return -1;
    if (!isAFuture && isBFuture) return 1;
    
    // If both are future, sort by closest date first
    if (isAFuture && isBFuture) {
      return dateA.getTime() - dateB.getTime();
    }
    
    // If both are past, sort by most recent first
    if (!isAFuture && !isBFuture) {
      return dateB.getTime() - dateA.getTime();
    }
    
    // Define status priorities for future events
    const getStatusPriority = (status: string) => {
      switch (status) {
        case 'pendente': return 1;
        case 'confirmado': return 2;
        case 'concluido': return 3;
        case 'cancelado': return 4;
        default: return 1;
      }
    };
    
    const statusPriorityA = getStatusPriority(a.status || 'pendente');
    const statusPriorityB = getStatusPriority(b.status || 'pendente');
    
    // If both are future and have different statuses, sort by status priority
    if (isAFuture && isBFuture && statusPriorityA !== statusPriorityB) {
      return statusPriorityA - statusPriorityB;
    }
    
    // Default sorting by date
    return dateA.getTime() - dateB.getTime();
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContracts = sortedContracts.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.status, filters.payment, filters.search]);

  // Calculate statistics
  const stats = {
    total: contracts.length,
    confirmados: contracts.filter(c => c.status === 'confirmado').length,
    pendentes: contracts.filter(c => c.status === 'pendente').length,
    orcamentos: contracts.filter(c => c.status === 'orcamento').length,
    concluidos: contracts.filter(c => c.status === 'concluido').length,
    pagos: contracts.filter(c => c.pago).length,
    pendentesPagamento: contracts.filter(c => !c.pago).length,
    valorTotal: contracts.reduce((sum, c) => sum + (parseFloat(c.valor || '0') || 0), 0)
  };

  // Handle contract selection
  const handleContractSelect = (contract: Contract) => {
    const isSelected = selectedContracts.some(c => c.id === contract.id);
    if (isSelected) {
      setSelectedContracts(selectedContracts.filter(c => c.id !== contract.id));
    } else {
      setSelectedContracts([...selectedContracts, contract]);
    }
  };

  // Handle batch actions
  const handleBatchAction = async (action: string) => {
    if (selectedContracts.length === 0) return;

    try {
      switch (action) {
        case 'confirm':
          await Promise.all(selectedContracts.map(c => 
            updateContract(c.id!, { status: 'confirmado' })
          ));
          break;
        case 'cancel':
          await Promise.all(selectedContracts.map(c => 
            updateContract(c.id!, { status: 'cancelado' })
          ));
          break;
        case 'complete':
          await Promise.all(selectedContracts.map(c => 
            updateContract(c.id!, { status: 'concluido' })
          ));
          break;
        case 'markPaid':
          await Promise.all(selectedContracts.map(c => 
            updateContract(c.id!, { pago: true })
          ));
          break;
        case 'markUnpaid':
          await Promise.all(selectedContracts.map(c => 
            updateContract(c.id!, { pago: false })
          ));
          break;
        case 'delete':
          if (window.confirm(`Tem certeza que deseja excluir ${selectedContracts.length} contratos?`)) {
            await Promise.all(selectedContracts.map(c => 
              deleteContract(c.id!)
            ));
          }
          break;
      }
      
      await refreshContracts();
      setSelectedContracts([]);
      setShowBatchActions(false);
    } catch (error) {
      console.error('Erro ao executar ação em lote:', error);
    }
  };

  // Handle individual contract actions
  const handleEdit = (contract: Contract) => {
    // Implement edit modal
    console.log('Editar contrato:', contract);
  };

  const handleView = (contract: Contract) => {
    // Implement view modal
    console.log('Visualizar contrato:', contract);
  };

  const handleDelete = async (contract: Contract) => {
    if (window.confirm('Tem certeza que deseja excluir este contrato?')) {
      try {
        deleteContract(contract.id!);
        await refreshContracts();
      } catch (error) {
        console.error('Erro ao excluir contrato:', error);
      }
    }
  };

  const handleGeneratePDF = async (contract: Contract) => {
    try {
      window.open(`/api/contracts/${contract.id}/pdf`, '_blank');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    }
  };

  const handleStatusChange = async (contract: Contract, status: Contract['status']) => {
    try {
      updateContract(contract.id!, { status });
      await refreshContracts();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handlePaymentToggle = async (contract: Contract, paid: boolean) => {
    try {
      updateContract(contract.id!, { pago: paid });
      await refreshContracts();
    } catch (error) {
      console.error('Erro ao atualizar pagamento:', error);
    }
  };

  // DataTable columns for list view
  const columns: Column<Contract>[] = [
    {
      key: 'contratante',
      label: 'Contratante',
      sortable: true,
      render: (value, contract) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
            <Users className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">{contract.contratante}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{contract.cpf}</div>
          </div>
        </div>
      )
    },
    {
      key: 'dataFesta',
      label: 'Data da Festa',
      sortable: true,
      render: (value) => value ? formatDate(value) : 'Não definida'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, contract) => {
        const statusConfig = {
          confirmado: { color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Confirmado' },
          pendente: { color: 'text-amber-600', bg: 'bg-amber-50', label: 'Pendente' },
          cancelado: { color: 'text-red-600', bg: 'bg-red-50', label: 'Cancelado' },
          orcamento: { color: 'text-blue-600', bg: 'bg-blue-50', label: 'Orçamento' },
          concluido: { color: 'text-purple-600', bg: 'bg-purple-50', label: 'Concluído' }
        };
        const config = statusConfig[contract.status] || statusConfig.pendente;
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
            {config.label}
          </span>
        );
      }
    },
    {
      key: 'valor',
      label: 'Valor',
      sortable: true,
      render: (value) => !value ? (
        <span className="text-blue-600 font-semibold">Pendente de Orçamento</span>
      ) : (
        formatCurrency(value)
      )
    },
    {
      key: 'pago',
      label: 'Pagamento',
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
        }`}>
          {value ? 'Pago' : 'Pendente'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (_, contract) => (
        <div className="flex items-center gap-2">
          {!contract.valor ? (
            <div className="flex flex-col items-start">
              <button
                onClick={() => navigate(`/admin/contratos/orcamento/${contract.id}`)}
                className="flex items-center gap-1 px-2 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors text-xs"
                title="Montar Orçamento"
              >
                <FileText className="w-3 h-3" />
                Orçamento
              </button>
              <button
                onClick={() => navigate(`/admin/contratos/${contract.id}`)}
                className="text-xs text-gray-500 hover:text-gray-700 hover:underline mt-1"
                title="Ver Detalhes"
              >
                Ver Detalhes
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate(`/admin/contratos/${contract.id}`)}
              className="p-2 text-indigo-600 hover:text-indigo-800 transition-colors"
              title="Ver Detalhes"
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => handleDelete(contract)}
            className="p-2 text-red-600 hover:text-red-800 transition-colors"
            title="Excluir"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4 overflow-x-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 shadow-lg shadow-teal-200 ${entranceAnimations.slideInFromTop}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <button className={`px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30 ${baseTransitions.normal} ${buttonAnimations.hover} ${buttonAnimations.press}`}>
              <Plus className="w-4 h-4" />
              Novo Contrato
            </button>
            
            <div>
              <h1 className="text-2xl font-bold text-white font-display">Contratos</h1>
              <p className="text-teal-100 font-admin">Gerencie todos os seus contratos de eventos</p>
            </div>
          </div>
        </div>
      </div>

                {/* Statistics Cards */}
          <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 relative ${entranceAnimations.fadeIn}`}>
            {showSkeletons ? (
              <>
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
              </>
            ) : (
              <>
                <StatsCard
                  title="Total de Contratos"
                  value={stats.total}
                  icon={FileText}
                  iconColor="text-slate-600"
                  iconBgColor="bg-slate-100"
                  className="col-span-1"
                  animate={true}
                  animationDelay={0}
                />
                
                <StatsCard
                  title="Confirmados"
                  value={stats.confirmados}
                  icon={CheckCircle}
                  iconColor="text-emerald-600"
                  iconBgColor="bg-emerald-50"
                  className="col-span-1"
                  animate={true}
                  animationDelay={200}
                />
                
                <StatsCard
                  title="Pendentes"
                  value={stats.pendentes}
                  icon={Clock}
                  iconColor="text-amber-600"
                  iconBgColor="bg-amber-50"
                  className="col-span-1"
                  animate={true}
                  animationDelay={400}
                />
                
                <StatsCard
                  title="Concluídos"
                  value={stats.concluidos}
                  icon={Star}
                  iconColor="text-slate-600"
                  iconBgColor="bg-slate-100"
                  className="col-span-1"
                  animate={true}
                  animationDelay={600}
                />
                
                <StatsCard
                  title="Valor Total"
                  value={formatCurrency(stats.valorTotal)}
                  icon={DollarSign}
                  iconColor="text-emerald-600"
                  iconBgColor="bg-emerald-50"
                  className="col-span-2"
                  showIcon={false}
                  animate={true}
                  animationDelay={800}
                />
              </>
            )}
        
        {/* Decorative Image - Moana (Floating) */}
        {!showSkeletons && (
          <div className="absolute -right-16 bottom-0 hidden 2xl:block">
            <div className="w-64 h-64">
              <img 
                src="/images/moana.png"
                alt="Moana - PlayKids"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
          </div>
        )}
      </div>

      {/* Filters and View Controls */}
      <div className={`bg-white rounded-xl border border-slate-200 shadow-sm p-5 ${entranceAnimations.slideInFromBottom}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className={`px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${baseTransitions.normal}`}
            >
              <option value="">Todos os Status</option>
              <option value="orcamento">Orçamento</option>
              <option value="pendente">Pendente</option>
              <option value="confirmado">Confirmado</option>
              <option value="concluido">Concluído</option>
              <option value="cancelado">Cancelado</option>
            </select>
            
            <select
              value={filters.payment}
              onChange={(e) => setFilters(prev => ({ ...prev, payment: e.target.value }))}
              className={`px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${baseTransitions.normal}`}
            >
              <option value="">Todos os Pagamentos</option>
              <option value="pago">Pagos</option>
              <option value="pendente">Pendentes</option>
            </select>
            
            <input
              type="text"
              placeholder="Buscar contratos..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className={`px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 min-w-[200px] ${baseTransitions.normal}`}
            />
          </div>

          {/* View Controls */}
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${baseTransitions.normal} ${buttonAnimations.hover} ${
                viewMode === 'grid' 
                  ? 'bg-white text-teal-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${baseTransitions.normal} ${buttonAnimations.hover} ${
                viewMode === 'list' 
                  ? 'bg-white text-teal-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Batch Actions */}
      {selectedContracts.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedContracts.length} selecionados
              </span>
            </div>
            
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => handleBatchAction('confirm')}
                className="px-2 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 transition-colors"
              >
                Confirmar
              </button>
              
              <button
                onClick={() => handleBatchAction('complete')}
                className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 transition-colors"
              >
                Concluir
              </button>
              
              <button
                onClick={() => handleBatchAction('markPaid')}
                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
              >
                Pagar
              </button>
              
              <button
                onClick={() => handleBatchAction('delete')}
                className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contracts Display */}
              {viewMode === 'grid' ? (
          <>
            {/* Grid View */}
            {showSkeletons ? (
              <CardsGridSkeleton />
            ) : (
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 ${entranceAnimations.fadeIn}`}>
                {paginatedContracts.map((contract) => (
                  <ContractCard
                    key={contract.id}
                    contract={contract}
                    selected={selectedContracts.some(c => c.id === contract.id)}
                    onSelect={handleContractSelect}
                    onEdit={handleEdit}
                    onView={handleView}
                    onDelete={handleDelete}
                    onGeneratePDF={handleGeneratePDF}
                    onStatusChange={handleStatusChange}
                    onPaymentToggle={handlePaymentToggle}
                  />
                ))}
              </div>
            )}
          
          {/* Pagination for Grid View */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-8">
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-2 bg-white rounded-xl border border-slate-200 shadow-sm px-3 sm:px-4 py-3 w-full max-w-md sm:max-w-none">
                {/* Mobile: Previous/Next buttons */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed ${baseTransitions.normal} ${buttonAnimations.hover}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <span className="text-sm text-slate-500 sm:hidden">
                    Página {currentPage} de {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed ${baseTransitions.normal} ${buttonAnimations.hover}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Desktop: Page numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${baseTransitions.normal} ${buttonAnimations.hover} ${
                        currentPage === page
                          ? 'bg-teal-500 text-white'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                {/* Desktop: Item count */}
                <span className="hidden sm:block text-sm text-slate-500 ml-4">
                  {startIndex + 1}-{Math.min(endIndex, filteredContracts.length)} de {filteredContracts.length}
                </span>
              </div>
            </div>
          )}
        </>
              ) : (
          /* List View */
          showSkeletons ? (
            <DataTableSkeleton />
          ) : (
            <DataTable
              data={filteredContracts}
              columns={columns}
              keyField="id"
              selectable
              onRowSelect={setSelectedContracts}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
              loading={loading}
              emptyMessage="Nenhum contrato encontrado"
            />
          )
        )}

      {/* Empty State */}
      {!loading && !showSkeletons && filteredContracts.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nenhum contrato encontrado
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Tente ajustar os filtros ou criar um novo contrato.
          </p>
          <button className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 ${baseTransitions.normal} ${buttonAnimations.hover} ${buttonAnimations.press}`}>
            Criar Contrato
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardNew;
